import React, { useEffect, useRef, useState } from "react";
import { Box, Button, Dialog, DialogActions, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { ActionCellItems } from "./ActionCellItems";
import { ShipmentsLogsDrawer } from "./ShipmentsLogsDrawer/ShipmentsLogsDrawer";
import { CustomCellRendererProps } from "ag-grid-react";
import { useCustomPackage } from "../../hooks/useCustomPackage";
import { PrintByItemValidationModalContent } from "../PrintByItem/PrintByItemValidationModalContent";
import { CustomerDetailData } from "../../interfaces/ICustomerDetails";
import {
  AUTHENTICATED_EMPLOYEE_JWT,
  IS_AUTO_PRINT,
  IS_BUNDLE_VIEW,
  PICK_AND_PACK_STATUS,
  SEARCH_TEXT,
  SHIPMENTS_TAB,
  SHIPMENTS_TABLE_TABS,
  SHIPPED_STATUS,
} from "../../constants";
import { Drawer } from "../Drawer";
import { globalTheme } from "../../styles";
import { CreateShipment } from "../CreateShipment/CreateShipment";
import { usePrinter, useUpdateAction } from "../../hooks";
import { BulkActionsDrawer } from "./BulkActionsDrawer/BulkActionsDrawer";
import { useAmazonWIrefresh } from "../../hooks/useAmazonWIRefresh";
import { useGetPDF } from "../../hooks/usegetPDF";

interface ActionCellCellProps extends CustomCellRendererProps {
  refreshData: () => void;
  setSnackBar: (snackBar: { open: boolean; message: string; severity: string }) => void;
}

/**
 * This ActionCell Component
 * consist of Icons under the Action column
 */

export const ActionCell = React.memo((params: ActionCellCellProps) => {
  const { data, refreshData, setSnackBar, api } = params;
  const [shipmentLogsDrawerOpen, setShipmentLogsDrawerOpen] = useState(false);
  const [createShipmentDrawerOpen, setCreateShipmentDrawerOpen] = useState(false);
  const [openValidationsModal, setOpenValidationsModal] = useState<boolean>(false);
  const [isAutoPrint, setIsAutoPrint] = useState(localStorage.getItem(IS_AUTO_PRINT) === "true" ? true : false);
  const [openActionsDrawer, setOpenActionsDrawer] = useState<boolean>(false);
  const [isIconDisabled, setIsIconDisabled] = useState<boolean>(false);
  //Data that is passed to pdf Generator
  const [pdfGenerationData, setPdfGenerationData] = useState({
    orderIds: data.orderIds,
    shippingFirstName: data.shippingFirstName,
    shippingLastName: data.shippingLastName,
    shippingAddressLine1: data.shippingAddressLine1,
    shippingAddressLine2: data.shippingAddressLine2,
    shippingCity: data.shippingCity,
    shippingStateOrProvince: data.shippingStateOrProvince,
    shippingStateOrProvinceName: data.shippingStateOrProvinceName,
    shippingPostalCode: data.shippingPostalCode,
    shippingCountry: data.shippingCountry,
    billingFirstName: data.billingFirstName,
    billingLastName: data.billingLastName,
    billingCompanyName: data.billingCompanyName,
    billingAddressLine1: data.billingAddressLine1,
    billingAddressLine2: data.billingAddressLine2,
    billingCity: data.billingCity,
    billingStateOrProvince: data.billingStateOrProvince,
    billingStateOrProvinceName: data.billingStateOrProvinceName,
    billingPostalCode: data.billingPostalCode,
    billingCountry: data.billingCountry,
    siteOrderIDs: data.siteOrderIDs,
    items: data.items,
  });

  useEffect(() => {
    setPdfGenerationData({
      orderIds: data.orderIds,
      shippingFirstName: data.shippingFirstName,
      shippingLastName: data.shippingLastName,
      shippingAddressLine1: data.shippingAddressLine1,
      shippingAddressLine2: data.shippingAddressLine2,
      shippingCity: data.shippingCity,
      shippingStateOrProvince: data.shippingStateOrProvince,
      shippingStateOrProvinceName: data.shippingStateOrProvinceName,
      shippingPostalCode: data.shippingPostalCode,
      shippingCountry: data.shippingCountry,
      billingFirstName: data.billingFirstName,
      billingLastName: data.billingLastName,
      billingCompanyName: data.billingCompanyName,
      billingAddressLine1: data.billingAddressLine1,
      billingAddressLine2: data.billingAddressLine2,
      billingCity: data.billingCity,
      billingStateOrProvince: data.billingStateOrProvince,
      billingStateOrProvinceName: data.billingStateOrProvinceName,
      billingPostalCode: data.billingPostalCode,
      billingCountry: data.billingCountry,
      siteOrderIDs: data.siteOrderIDs,
      items: data.items,
    });
  }, [data]);

  const prepareActionUpdateRequest = (items) => {
    const actionUpdateRequest = items?.map((i) => {
      return {
        sku: i.sku,
        allQtsScanned: i.skuManuallyChecked ? false : true,
        skuManuallyChecked: i.skuManuallyChecked || false,
      };
    });

    return {
      itemValidatedList: actionUpdateRequest,
      actionType: "PRINTED",
      idList: [data._id],
      changes: [{ fieldName: "shippingStatus", oldValue: "", newValue: "PickAndPack" }],
    };
  };

  const [actionUpdateRequest, setActionUpdateRequest] = useState({ requestAvailable: false, request: [] });
  const { successfullUpdatedAction, updateActionResponseData } = useUpdateAction({
    updateActionRequest: actionUpdateRequest.request,
    enabled: actionUpdateRequest.requestAvailable,
  });

  useEffect(() => {
    if (updateActionResponseData?.code === "UNAUTHORIZED") {
      localStorage.removeItem(AUTHENTICATED_EMPLOYEE_JWT);
      window.location.reload();
    }
  }, [updateActionResponseData]);

  const handleDrawerClose = () => {
    setShipmentLogsDrawerOpen(false);
  };

  const handleCreateShipmentDrawerClose = () => {
    setCreateShipmentDrawerOpen(false);
  };

  const onValidationCreateShipmentDrawer = () => {
    setCreateShipmentDrawerOpen(false);
    setSnackBar({
      message: "Order is successfully moved to Pick and Pack",
      severity: "success",
      open: true,
    });

    refreshData();
  };

  const onErrorCreateShipmentDrawer = () => {
    setCreateShipmentDrawerOpen(false);

    setSnackBar({
      message: "Create shipment failed, Try again or contact support",
      severity: "error",
      open: true,
    });
  };

  const onCloseCreateShipmentDrawer = () => {
    setCreateShipmentDrawerOpen(false);
  };

  const onValidationDrawer = () => {
    setShipmentLogsDrawerOpen(false);
  };

  const onClickLogs = () => {
    setShipmentLogsDrawerOpen(true);
  };

  const handleValidationModalClose = () => {
    setOpenValidationsModal(false);
  };

  const transformedData = transformBundleOrders(data);

  const onItemPrintClick = () => {
    const shipmentsTab = localStorage.getItem(SHIPMENTS_TAB);
    if (
      (!data?.selectedQuote && !data?.shipmentDetails) ||
      (!data?.selectedQuote && data?.shipmentDetails && data?.shipmentDetails?.length === 0)
    ) {
      setCreateShipmentDrawerOpen(true);
    } else if (shipmentsTab === SHIPMENTS_TABLE_TABS.ALL_PICKED_ORDERS.value) {
      if (
        transformedData?.items?.length === 1 &&
        transformedData?.items[0]?.quantity === 1 &&
        data?.shipmentDetails &&
        data?.shipmentDetails[0]?.pdfLink &&
        localStorage.getItem(IS_AUTO_PRINT) === "true"
      ) {
        usePrinter(data?.shipmentDetails[0]?.pdfLink, data?.shipmentDetails[0]?.shippingCarrier);
        const actionUpdateRequest = prepareActionUpdateRequest(transformedData?.items);
        setActionUpdateRequest({ requestAvailable: true, request: [actionUpdateRequest] });
      } else {
        const autoPrintValue = localStorage.getItem(IS_AUTO_PRINT) === "true";
        setIsAutoPrint(autoPrintValue);

        setOpenValidationsModal(true);
      }
    } else {
      setCreateShipmentDrawerOpen(true);
    }
  };

  useEffect(() => {
    successfullUpdatedAction && onValidation();
  }, [successfullUpdatedAction]);

  const onValidation = () => {
    setOpenValidationsModal(false);
    setSnackBar({
      message: "Order is successfully moved to Pick and Pack",
      severity: "success",
      open: true,
    });
    refreshData();
  };

  const onError = () => {
    setOpenValidationsModal(false);

    setSnackBar({
      message: "Create shipment failed, Try again or contact support",
      severity: "error",
      open: true,
    });
  };

  const onShipmentClick = () => {
    setOpenActionsDrawer(true);
  };

  const [getPDFEnabled, setGetPDFEnabled] = useState<boolean>(false);

  const { loadingGetPDF, fetchingGetPDF, errorFetchingGetPDF, pdfBlob, fetchGetPDFError, refetchGetPDF } = useGetPDF({
    orderItemDataPdf: { ...pdfGenerationData },
    enabled: getPDFEnabled,
  });

  // Trigger fetching the PDF when print icon is clicked
  const onPrintIconClick = () => {
    setGetPDFEnabled(true);
  };

  useEffect(() => {
    setGetPDFEnabled(false);
  }, [getPDFEnabled]);

  const icons = ActionCellItems({
    onActionClick: onClickLogs,
    onItemPrintClick: onItemPrintClick,
    onShipmentClick: onShipmentClick,
    onPrintIcon: onPrintIconClick,
  });

  const mapDataToCustomerDetails = (data: any): CustomerDetailData => {
    return {
      name: `${data.shippingFirstName || ""} ${data.shippingLastName || ""}`.trim(),
      addressLine1: data?.shippingAddressLine1 || "",
      addressLine2: data?.shippingAddressLine2 || "",
      city: data?.shippingCity || "",
      companyName: data?.shippingCompanyName || "",
      country: data?.shippingCountry || "",
      phoneNumber: data?.shippingDaytimePhone || "",
      postalCode: data?.shippingPostalCode || "",
      stateOrProvince: data?.shippingStateOrProvince || "",
      email: data?.buyerEmailAddress || "",
    };
  };

  const handleSuccessfulUpdate = (updateActionResponseData) => {
    if (updateActionResponseData.success === true) {
      setOpenActionsDrawer(false);
      setSnackBar({
        open: true,
        message: "Action Update Success",
        severity: "success",
      });
      refreshData();
    } else {
      setOpenActionsDrawer(false);
      setSnackBar({
        open: true,
        message: " The Action update not success",
        severity: "error",
      });
    }
  };

  const [amazonWIrefreshPayloadData, setAmazonWIrefreshPayloadData] = useState();
  const [amazonWIrefetchEnable, setAmazonWIrefreshEnable] = useState<boolean>(false);

  //AmazonWi refresh API hook
  const { loadingAmazonWI, fetchingAmazonWI, errorFetchingAmazonWI, AmazonWIResponseData, fetchAmazonWIError, refetchAmazonWI } =
    useAmazonWIrefresh({ amazonWIdata: amazonWIrefreshPayloadData, enabled: amazonWIrefetchEnable });

  //Snackbar for the AmazonWI API response
  useEffect(() => {
    if (AmazonWIResponseData?.success === true) {
      setOpenActionsDrawer(false);
      setSnackBar({
        open: true,
        message: AmazonWIResponseData?.message,
        severity: "success",
      });
      refreshData();
    } else if (AmazonWIResponseData?.success === false) {
      setOpenActionsDrawer(false);
      setSnackBar({
        open: true,
        message: AmazonWIResponseData?.message,
        severity: "error",
      });
    }
  }, [AmazonWIResponseData, amazonWIrefetchEnable]);

  //Handle amazonWI api
  const handleOnSuccessfulAmazonWI = (selectedRows) => {
    setAmazonWIrefreshPayloadData(selectedRows);
    setAmazonWIrefreshEnable(true);
  };

  useEffect(() => {
    setAmazonWIrefreshEnable(false);
  }, [amazonWIrefetchEnable]);

  useEffect(() => {
    const updateIconDisabledState = () => {
      const selectedRows = api.getSelectedRows();
      setIsIconDisabled(selectedRows.length > 0);
    };

    api.addEventListener("selectionChanged", updateIconDisabledState);

    updateIconDisabledState();

    // Cleanup event listener on unmount
    return () => {
      api.removeEventListener("selectionChanged", updateIconDisabledState);
    };
  }, [api]);

  return (
    <Stack>
      <Drawer open={openActionsDrawer} onClose={() => setOpenActionsDrawer(!openActionsDrawer)}>
        <BulkActionsDrawer
          selectedRows={[data]}
          onSuccessfulUpdate={handleSuccessfulUpdate}
          onSuccessfulAmazonRefresh={handleOnSuccessfulAmazonWI}
        />
      </Drawer>
      <Stack sx={{ margin: 2 }} direction="row" alignItems="center">
        <div style={{ display: "flex", gap: "30px" }}>
          {icons.map(({ component: IconComponent, onClick, name }, index) => {
            const shipmentsTab = localStorage.getItem(SHIPMENTS_TAB);
            const isProcessShipmentIcon = index === 0;
            const isAmazonWI = data?.siteName === "Amazon WI";

            if (shipmentsTab === SHIPMENTS_TABLE_TABS.ERROR.value && isProcessShipmentIcon && isAmazonWI) {
              return null;
            }

            return (
              <Tooltip key={index} title={name} arrow>
                <IconButton key={index} onClick={onClick} disabled={index === 2 ? isIconDisabled : false}>
                  <IconComponent fill={index === 2 && isIconDisabled ? globalTheme.secondaryColor : globalTheme.primaryColor} />
                </IconButton>
              </Tooltip>
            );
          })}
        </div>

        <ShipmentsLogsDrawer
          key={data._id} // Use data._id instead of looping over orderIds
          open={shipmentLogsDrawerOpen}
          onClose={handleDrawerClose}
          onValidation={onValidationDrawer}
          shipmentLogsData={data.historyList}
          orderId={data._id}
        />

        <Drawer open={createShipmentDrawerOpen} onClose={handleCreateShipmentDrawerClose}>
          <Stack spacing={2} paddingX={2} paddingY={4}>
            <Typography
              variant="subtitle2"
              sx={{
                backgroundColor: globalTheme.background,
                color: globalTheme.primaryColor,
                p: 1,
                borderRadius: 2,
                width: "fit-content",
              }}
            >
              Manual shipment creation
            </Typography>
            <CreateShipment
              data={transformBundleOrders(data)}
              orderId={data._id}
              CustomershipmentData={mapDataToCustomerDetails(data)}
              onValidation={onValidationCreateShipmentDrawer}
              onError={onErrorCreateShipmentDrawer}
              onClose={onCloseCreateShipmentDrawer}
            />
            <DialogActions>
              <Button onClick={onCloseCreateShipmentDrawer}>Close</Button>
            </DialogActions>
          </Stack>
        </Drawer>

        <Drawer open={openValidationsModal} onClose={handleValidationModalClose}>
          <Stack spacing={2} paddingX={2} paddingY={4}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                backgroundColor: globalTheme.background,
                color: globalTheme.primaryColor,
                p: 1,
                borderRadius: 2,
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  backgroundColor: globalTheme.background,
                  color: globalTheme.primaryColor,
                  p: 1,
                  borderRadius: 2,
                  width: "fit-content",
                }}
              >
                Validate & print items
              </Typography>
              <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                {data?.selectedQuote?.shipmentItems[0]?.parcelType}
              </Typography>
            </Box>
            <Box border={`1px solid ${globalTheme.border}`}>
              <PrintByItemValidationModalContent
                validatingItems={transformBundleOrders(data)}
                onValidation={onValidation}
                onError={onError}
                onClose={handleValidationModalClose}
                manualShipmentsData={null}
                firstItemBarcode={isAutoPrint ? localStorage.getItem(SEARCH_TEXT) : ""}
              />
            </Box>
            <DialogActions>
              <Button onClick={handleValidationModalClose}>Close</Button>
            </DialogActions>
          </Stack>
        </Drawer>
      </Stack>
    </Stack>
  );
});

const transformBundleOrders = (order) => {
  const isBundleView = JSON.parse(localStorage.getItem(IS_BUNDLE_VIEW)) || false;

  let orderClone = structuredClone(order);

  orderClone.items = isBundleView
    ? order.items
    : order.items.map((item) => (item.bundleComponents?.length ? item.bundleComponents : item)).flat(); // flat as inside map function

  return orderClone;
};
